import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { useApi } from '../composables/useApi.js'
import { useNotification } from '@kyvg/vue3-notification'
import { i18n } from '../i18n/index.js'
import { computed, ref } from 'vue'

export const useAuthStore = defineStore('authStore', () => {
  const $t = i18n.global.t
  const Api = useApi()
  const router = useRouter()
  const { notify } = useNotification()
  const firstEntry = useLocalStorage('first_entry', '')

  const loginMode = ref('email') // 'email' | 'phone'
  const isSigned = ref(false)
  const isPassword = ref(false)
  const isRegistered = ref(false)
  const token = ref('')
  const isValidPhone = ref(false)
  const user = ref(null)
  const email = ref('')
  const phone = ref('')
  // Set from POST auth/login so the OTP screen knows whether to require the
  // terms checkbox - the SPA otherwise only learns this from auth/pin, after
  // the code is already verified.
  const isNewUser = ref(false)
  const termsAccepted = ref(false)

  const isLogged = computed(() => !!token.value)
  const getLang = computed(() => user.value?.current_language ?? 'en')
  const currentLocale = useLocalStorage('locale', getLang.value)

  const saveToken = () => {
    const storage = isSigned.value ? localStorage : sessionStorage
    storage.setItem('access_token', token.value)
  }

  const initToken = () => {
    const localToken = localStorage.getItem('access_token') || ''
    const sessionToken = sessionStorage.getItem('access_token') || ''

    if (localToken) {
      token.value = localToken
    } else if (sessionToken) {
      token.value = sessionToken
    }
  }
  initToken()

  const logout = async () => {
    token.value = ''
    localStorage.removeItem('access_token')
    sessionStorage.removeItem('access_token')
    await router.push({ name: 'login_email' })
    user.value = null
  }

  const login = async (params) => {
    const payload = {
      referalCode: params.refCode,
      inviteCode: params.inviteCode,
      captchaToken: params.captchaToken
    }

    if (params.email) {
      email.value = params.email
      payload.email = params.email
    }

    if (params.phone) {
      phone.value = params.phone
      payload.phone = params.phone
    }

    isSigned.value = params.isSigned

    const { data, error } = await Api('auth/login').post(payload).json()

    if (error.value) {
      notify({
        title: $t('error_login'),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }

    isNewUser.value = !!data.value?.data?.isNewUser
    termsAccepted.value = false

    router.push({ name: 'pin' })
    return true
  }

  const confirmCode = async (params) => {
    const { data, error } = await Api('auth/pin').post(params).json()
    isRegistered.value = data.value?.data.isRegistered
    isPassword.value = data.value?.data.isPassword

    if (data.value?.data) {
      if ((!isPassword.value && !isRegistered.value) || isPassword.value) {
        token.value = data.value?.data.temporaryToken
      } else {
        token.value = data.value?.data.token
      }

      email.value = data.value?.data.mail

      let routeName = 'create_password'
      if (!isRegistered.value) {
        routeName = 'registration'
      }
      if (isRegistered.value && data.value?.data.token) {
        routeName = 'home'
      }
      if (isPassword.value) {
        routeName = 'password'
      }
      router.push({ name: routeName })

      return true
    }

    if (error.value) {
      notify({
        title: $t('error_pin'),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }
  }

  const registration = async (params) => {
    // Stamped server-side from this flag; the API rejects registration without
    // it. Injected here rather than at the call site so a future caller cannot
    // forget it.
    const { data, error } = await Api('users/register')
      .post({ ...params, terms_and_policy_accepted: termsAccepted.value })
      .json()

    if (data?.value) {
      token.value = data.value?.data?.token
      saveToken()
    }
    if (error.value) {
      notify({
        title: $t('error_registration'),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }

    firstEntry.value = true
    router.push({ name: 'home' })
  }

  const confirmPass = async (params) => {
    const { data, error } = await Api('auth/pass').post(params).json()
    if (data.value?.data.token) {
      token.value = data.value.data.token
      saveToken()

      router.push({ name: 'home' })

      return true
    }

    if (error.value) {
      notify({
        title: $t('error_pass'),
        type: 'error',
        ignoreDuplicates: true
      })
      return false
    }
  }

  const current = async () => {
    const { data } = await Api('users/current').get().json()
    if (data.value?.data) {
      user.value = data.value.data
      i18n.global.locale.value = currentLocale.value
      return user.value
    }

    // TODO: need to fix this logout.
    // if user use quick f5 several times, he will be logouted

    logout()
    return false
  }

  const acceptTerms = async () => {
    const { error } = await Api('users/accept-terms').post().json()

    if (error.value) {
      notify({
        title: $t('error_occurred'),
        type: 'error',
        ignoreDuplicates: true
      })
      return false
    }

    // Re-read rather than patching the local copy, so the gate is cleared by
    // what the server actually recorded.
    await current()
    return true
  }

  const forgotPass = async () => {
    const { data } = await Api('auth/forgot-password').post().json()

    if (data.value) {
      return data.value
    } else {
      notify({
        title: $t('error_occurred'),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }
  }

  const confirmForgot = async (params) => {
    const { data, error } = await Api('auth/confirm-forgot-password')
      .post(params)
      .json()

    if (data.value?.data.token) {
      token.value = data.value.data.token
      saveToken()

      notify({
        title: $t('success'),
        text: $t('reset_success'),
        type: 'success',
        ignoreDuplicates: true
      })

      router.push({ name: 'home' })

      return true
    }

    if (error.value) {
      notify({
        title: $t('error_occurred'),
        type: 'error',
        ignoreDuplicates: true
      })
      return 'error'
    }
  }

  return {
    logout,
    login,
    confirmCode,
    confirmPass,
    registration,
    confirmForgot,
    forgotPass,
    current,
    acceptTerms,
    saveToken,
    token,
    isLogged,
    phone,
    user,
    email,
    firstEntry,
    currentLocale,
    isValidPhone,
    loginMode,
    isPassword,
    isNewUser,
    termsAccepted
  }
})
